import { Box, FormControlLabel, Grid, Radio, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { infiniteListPageSize } from "source/models/common/webConsts";
import * as userGroupService from "source/models/services/userGroupService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { List } from "source/components/framework/List/List";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { UserGroupType } from "ui/Scripts/source/models/enums";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

interface ISelectUserGroupDialogListProps {
    companyId: string;
    height?: string;
    defaultUserGroupType?: UserGroupType;
    initiallySelectedUserGroupCodes?: string[];
    ignoreUserGroupCodes?: string[];
    requireUserGroupCodes?: string[];
    ignoreQrCodeUserGroups?: boolean;
    requireQrCodeUserGroups?: boolean
    messageTargetMode?: boolean;
    listModeChangeDisabled?: boolean;
    multiSelect?: boolean;
    intialized: boolean;
    selectedId: string;
    checkedIds: string[];
    onSetIntialized: (intialized: boolean) => void;
    onSetSelected: (selectedId: string, checkedIds: string[], selectedItems: IUserGroupListItem[], userGroupType: UserGroupType) => void;
    onGetUserGroupListItems?: (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
        ignoreUserGroupCodes: string[], requireUserGroupCodes: string[], ignoreQrCodeUserGroups: boolean, requireQrCodeUserGroups: boolean, messageTargetMode: boolean) => Promise<UserGroupListItems>;
}

interface ISelectUserGroupDialogListState {
    isLoading: boolean;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    userGroupType: UserGroupType;
    items: IUserGroupListItem[];
}

const pageSize = 10000;

export const SelectUserGroupDialogList = (props: ISelectUserGroupDialogListProps) => {
    const _isMounted = useRef<boolean>(false);
    const isInitialized = useRef<boolean>(false);

    const [state, setState] = useState<ISelectUserGroupDialogListState>({
        isLoading: false,
        page: 1,
        hasMore: true,
        filter: "",
        sortColumn: "name",
        sortOrderIsAsc: true,
        userGroupType: props.defaultUserGroupType ?? UserGroupType.Normal,
        items: []
    });

    const setSelected = (selectedId: string, checkedIds: string[], items: IUserGroupListItem[], userGroupType: UserGroupType) => {
        const ids = List.getSelectedIds(selectedId, checkedIds);
        props.onSetSelected(selectedId, checkedIds, items.filter(i => ids.indexOf(i.id) > -1), userGroupType);
    };

    const searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, userGroupType: UserGroupType, resetItems: boolean, refreshList: boolean) => {
        if (!_isMounted.current) return;
        setState(old => ({ ...old, isLoading: true }));
        const getUserGroupListItems = props.onGetUserGroupListItems ?? userGroupService.getUserGroupListItemsForPermissions;
        const result = await AppUtils.callService(() => getUserGroupListItems(props.companyId, !refreshList ? pageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc,
            userGroupType, props.ignoreUserGroupCodes, props.requireUserGroupCodes, props.ignoreQrCodeUserGroups, props.requireQrCodeUserGroups, props.messageTargetMode), false, true, () => { if (!_isMounted.current) return; setState(old => ({ ...old, isLoading: false })); });
        if (!result) return;
        const initialCheckedIds = !isInitialized.current && props.initiallySelectedUserGroupCodes?.length
            ? result.items.filter(i => props.initiallySelectedUserGroupCodes.includes(i.code)).map(i => i.id)
            : [];
        if (!_isMounted.current) return;
        const items = List.getListItems(state.items, props.selectedId, props.checkedIds.concat(initialCheckedIds), result.items, resetItems, refreshList);
        setState(old => {
            return {
                ...old,
                page: !refreshList ? result.page : page,
                hasMore: result.hasMore,
                filter,
                sortColumn,
                sortOrderIsAsc,
                userGroupType,
                items: items.items
            };
        });
        isInitialized.current = true;
        props.onSetIntialized(true);
        setSelected(items.selectedId, items.checkedIds, items.items, userGroupType);
    };

    useEffect(() => {
        _isMounted.current = true;
        
        return () => {
            _isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        searchItems(pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, state.userGroupType, true, false);
    }, [
        props.companyId ?? "",
        props.ignoreUserGroupCodes?.filter(i => i !== "0")?.join(",") ?? "",
        props.requireUserGroupCodes?.filter(i => i !== "0")?.join(",") ?? "",
        props.ignoreQrCodeUserGroups ?? false,
        props.requireQrCodeUserGroups ?? false,
        props.messageTargetMode ?? false
    ]);

    const changeFilter = (filter: string) => {
        searchItems(pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, state.userGroupType, true, false);
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        searchItems(pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, state.userGroupType, !nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setSelected(selectedId, checkedIds, state.items, state.userGroupType);
    };

    const handleChangeUserGroupType = (userGroupType: UserGroupType) => {
        searchItems(pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, userGroupType, true, false);
    };

    return (
        <VerticalContainer
            before={<>
                {!props.listModeChangeDisabled &&
                    <GridRow margin="-16px 0 0 -16px">
                        <Grid item sm={6}>
                            <FormControlLabel
                                label={Translations.Users}
                                control={
                                    <Radio
                                        checked={state.userGroupType > 0.5}
                                        onChange={(_e, checked: boolean) => {
                                            handleChangeUserGroupType(checked ? UserGroupType.Fixed : UserGroupType.Normal);
                                        }}
                                    />
                                }
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <FormControlLabel
                                label={Translations.UserGroups}
                                control={
                                    <Radio
                                        checked={state.userGroupType < 0.5}
                                        onChange={(_e, checked: boolean) => {
                                            handleChangeUserGroupType(checked ? UserGroupType.Normal : UserGroupType.Fixed);
                                        }}
                                    />
                                }
                            />
                        </Grid>
                    </GridRow>
                }
                <ActionContainer
                    leftItems={
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={changeFilter}
                        />
                    }
                />
            </>}
            main={<>
                <List
                    multiSelect={props.multiSelect}
                    intialized={props.intialized}
                    isLoading={state.isLoading}
                    hasMore={state.hasMore}
                    height={props.height ?? "100%"}
                    header={state.userGroupType === UserGroupType.Normal
                        ? {
                            columns: [
                                { size: 6, column: "name", content: Translations.Name },
                                { size: 6, column: "comment", content: Translations.Description },
                            ]
                        }
                        : {
                            columns: [
                                { size: 12, column: "name", content: Translations.Name }
                            ]
                        }}
                    lines={state.userGroupType === UserGroupType.Normal
                        ? state.items.map((item) => {
                            return {
                                columns: [
                                    { size: 6, column: "name", content: item.name },
                                    { size: 6, content: item.comment },
                                ],
                                id: item.id
                            };
                        })
                        : state.items.map((item) => {
                            return {
                                columns: [
                                    {
                                        size: 12,
                                        content: <Box display="flex" alignItems="center">
                                            <AvatarImage
                                                id={item.code}
                                                name={item.name}
                                                image={item.picture}
                                                tooltip={item.name}
                                            />
                                            <Typography sx={{ marginLeft: ".5rem" }}>{item.name}</Typography>
                                        </Box>
                                    }
                                ],
                                id: item.id
                            };
                        })}
                    sortColumn={state.sortColumn}
                    sortOrderIsAsc={state.sortOrderIsAsc}
                    selectedId={props.selectedId}
                    checkedIds={props.checkedIds}
                    onSetSelectedIds={handleSetSelectedIds}
                    onSearchLines={handleSearchLines}
                />
            </>}
        />
    );
};
